import { useEffect, useRef, useState } from "react";
import { Briefcase, Plus } from "lucide-react";
import { ExperienceCard } from "./ExperienceCard";
import { EntryRail } from "./EntryRail";
import type { WorkExperience } from "./types";
import { hasIssues, validateExperienceEntry, visibleErrors } from "./validation";

interface ExperienceStepProps {
  items: WorkExperience[];
  onAdd: () => void;
  onUpdate: (id: string, field: keyof WorkExperience, value: string | boolean) => void;
  onRemove: (id: string) => void;
  /** Also show "required" / "too short" messages (after a Continue attempt). */
  showAllErrors?: boolean;
}

// Any problem — missing, too long, dates out of order — flags the entry in the
// rail, so an over-limit role the resume parser filled in is findable without
// paging through every entry.
const isIncomplete = (exp: WorkExperience) => hasIssues(validateExperienceEntry(exp));

export function ExperienceStep({
  items,
  onAdd,
  onUpdate,
  onRemove,
  showAllErrors = false,
}: ExperienceStepProps) {
  // Master–detail: the rail picks one entry; only that entry's form renders,
  // so the step height is fixed no matter how many roles the resume produced.
  const [activeId, setActiveId] = useState<string | null>(items[0]?.id ?? null);
  const prevLen = useRef(items.length);

  useEffect(() => {
    const len = items.length;
    if (len > prevLen.current) {
      setActiveId(items[len - 1]?.id ?? null); // newly added → focus it
    } else if (len === 0) {
      setActiveId(null);
    } else if (!items.some((i) => i.id === activeId)) {
      setActiveId(items[0]?.id ?? null); // active was removed → fall back to first
    }
    prevLen.current = len;
  }, [items, activeId]);

  if (items.length === 0) {
    return (
      <div
        className="flex flex-col items-center gap-3 rounded-2xl border border-dashed p-10 text-center"
        style={{ borderColor: "var(--landing-glass-border)" }}
      >
        <div
          className="flex h-12 w-12 items-center justify-center rounded-xl"
          style={{ background: "rgba(var(--acc2-rgb),0.08)", border: "1px solid rgba(var(--acc2-rgb),0.18)" }}
        >
          <Briefcase className="h-5 w-5 text-[var(--acc2)]" />
        </div>
        <p className="text-sm font-medium" style={{ color: "var(--landing-text)" }}>
          Add at least one role
        </p>
        <p className="max-w-xs text-xs" style={{ color: "var(--landing-text-muted)" }}>
          Recruiters look here first. Lead with your most recent and relevant experience.
        </p>
        <button
          type="button"
          onClick={onAdd}
          className="btn-electric mt-1 inline-flex h-10 items-center justify-center gap-2 rounded-xl px-5 text-sm font-semibold"
        >
          <Plus className="h-4 w-4" />
          Add experience
        </button>
      </div>
    );
  }

  const active = items.find((i) => i.id === activeId) ?? items[0];
  const activeIndex = items.findIndex((i) => i.id === active.id);
  const activeIssues = validateExperienceEntry(active);

  return (
    <div className="space-y-4">
      <EntryRail
        items={items.map((it) => ({
          id: it.id,
          title: it.company || "New role",
          subtitle: it.position || "—",
          incomplete: isIncomplete(it),
        }))}
        activeId={active.id}
        accent="var(--acc2)"
        addLabel="Add role"
        onSelect={setActiveId}
        onAdd={onAdd}
      />

      <ExperienceCard
        index={activeIndex}
        item={active}
        incomplete={hasIssues(activeIssues)}
        errors={visibleErrors(activeIssues, showAllErrors)}
        onUpdate={(field, value) => onUpdate(active.id, field, value)}
        onRemove={() => onRemove(active.id)}
      />
    </div>
  );
}
